import * as FluxUtils from 'flux/utils';
import { t } from 'i18next';

import { AppDispatcher, DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { GlobalHelper } from '../utils/GlobalHelper';
import { ProjectManagerStore } from './ProjectManagerStore';
import { ProjectSettingsStore } from './ProjectSettingsStore';
import { PriceStore } from './PriceStore';
import { UIHelper } from '../utils/UIHelper';
import { ArticleListItem } from '../components/sidebar/articlelist/ArticleListItem';

import cf = egr.wcf.cf;
import eaiws = egr.wcf.eaiws;
import utils = egr.wcf.utils;

export interface ArticleItem {
    article: cf.ArticleElement;
    properties: eaiws.basket.ItemProperties;
    image: string | null;
}

interface ArticleStoreState {
    needsUpdate: boolean;
    articleCount: number;
    articleList: Array<ArticleItem>;
}

class ArticleStoreClass extends FluxUtils.ReduceStore<ArticleStoreState, unknown> {
    public getInitialState(): ArticleStoreState {
        return {
            needsUpdate: false,
            articleCount: 0,
            articleList: []
        };
    }

    public getStyledHTML(showPrices: boolean, showArticleNumber: boolean): string {
        const state: ArticleStoreState = this.getState();
        const articleList: Array<ArticleItem> = state.articleList;

        let title: string = ProjectManagerStore.getCurrentProjectName('');
        if (!title) {
            title = t('Article List');
        }
        // HTML Header
        const quantity: number = 1;
        let htmlContent: string = (
            '<!DOCTYPE html>\n' +
            '<html>\n' +
            '  <head>\n' +
            '    <meta http-equiv="X-UA-Compatible" content="IE=edge" />\n' +
            '    <meta<meta http-equiv="content-type" content="text/html; charset=ISO-8859-1">\n' +
            '    <title>' + title + '</title>\n' +
            '    <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">\n' +
            '  </head>\n' +
            '  <body style="font: normal 16px \'Open Sans\', sans-serif; padding: 0 16px; max-width: 640px; color: #4f4f4f;">\n'
        );

        // tslint:disable-next-line:max-line-length
        const pageHeadlineStyle: string = ' style="background: #fff; display: block; font-size: 24px; font-weight: 300; line-height: 62px; margin: 0 0 8px; padding: 0; position: relative;"';
        htmlContent += '    <div' + pageHeadlineStyle + '>' + t('Article List') + '</div>\n';

        let totalPrice: number = 0.0;
        const currency: string = ProjectSettingsStore.getState().currency;

        const articleItemContainers: Array<string> = articleList.map((article: ArticleItem) => {
            const articlePrice: number = ArticleListItem.getPrice(article.properties);
            totalPrice += articlePrice;
            let container: string = '<div style="padding-bottom: 10px; border-bottom: 1px solid #dadada; margin-bottom: 32px;">';
            const properties: eaiws.basket.ItemProperties = article.properties;
            if (properties.article == null) {
                return '';
            }

            if (article.image) {
                container += '    <div style="font-size: 16px; margin: 0; padding-bottom: 22px; position: relative; min-height: 60px; padding-left: 58px;">';
                // tslint:disable-next-line:max-line-length
                container += '      <div style="background-size: cover; height: 48px; width: 48px; background-image: url(' + article.image + '); position: absolute; top: 0; left: 0;"></div>';
            } else {
                container += '    <div style="font-size: 16px; margin: 0; padding-bottom: 22px; position: relative; min-height: 60px;">';
            }

            // Article-Header (articleNumber and shortText)
            const articleHeader: Array<string> = [];
            if (showArticleNumber) {
                const articleNumber: string | undefined = properties.article.useFinalArticleNumber ?
                    properties.article.finalArticleNumber : properties.article.baseArticleNumber;
                if (utils.string.isNotNullOrEmpty(articleNumber)) {
                    articleHeader.push(articleNumber);
                }
            }
            if (properties.article.shortText != null && properties.article.shortText !== '') {
                articleHeader.push(properties.article.shortText);
            }
            if (articleHeader.length) {
                container += '      <div style="text-overflow: ellipsis; overflow: hidden;">' + articleHeader.join(' | ') + '</div>\n';
            }

            // Manufacturer-Header (manufacturerName and seriesId)
            const manufacturerHeader: Array<string> = [];
            if (properties.article.manufacturerName != null) {
                manufacturerHeader.push(properties.article.manufacturerName);
            }
            if (properties.article.seriesId != null) {
                manufacturerHeader.push(properties.article.seriesId);
            }
            if (manufacturerHeader.length) {
                container += '      <div style="text-overflow: ellipsis; overflow: hidden;">' + manufacturerHeader.join(' | ') + '</div>\n';
            }
            container += '    </div>';

            // Article LongText
            if (properties.article.longText != null && properties.article.longText !== '') {
                container += '    <div>' + properties.article.longText + '</div>\n';
            }

            // featureText
            const features: Array<string> = [];
            if (properties.article.featureText != null) {
                UIHelper.multilineTextToArray(properties.article.featureText).map((featureString: string) => {
                    features.push('<li>' + featureString + '</li>\n');
                });
            }
            if (features.length) {
                container += '<ul>' + features.join('') + '</ul>';
            }

            if (
                showPrices &&
                properties.article.salesPrice != null &&
                utils.string.isNotNullOrEmpty(properties.article.currency)
            ) {
                const unitPrice: number = PriceStore.getPrice(properties.article.salesPrice, properties.article.currency);
                container += (
                    '<div style="height: 22px;"><div style="width: 50%; float: left; text-align: left;">' +
                    quantity +
                    ' x ' +
                    PriceStore.getPriceString(unitPrice, currency) +
                    '</div><div style="width: 50%; float: right; text-align: right;">' +
                    PriceStore.getPriceString(articlePrice, currency) +
                    '</div></div>'
                );
            }

            container += '</div>';

            return container;
        });

        htmlContent += articleItemContainers.join('');

        if (showPrices && totalPrice > 0.0) {
            htmlContent += (
                '<div style="height: 22px;"><div style="width: 50%; float: left; text-align: left;">' +
                t('Price (excl. Tax)') +
                '</div><div style="width: 50%; float: right; text-align: right;">' +
                PriceStore.getPriceString(totalPrice, currency) +
                '</div></div>'
            );
        }

        htmlContent += (
            '  </body>' +
            '</html>'
        );

        return htmlContent;
    }

    public reduce(state: ArticleStoreState, action: DispatcherBaseAction): ArticleStoreState {
        // Note: this function should be `pure` -> we have to return a copy
        // of the current state if some property was changed

        switch (action.actionType) {
            case ActionConstants.SET_LOADED_PROJECT:
                return jQuery.extend({}, state, {
                    articleCount: GlobalHelper.getArticleCount()
                });
            case ActionConstants.ARTICLE_LIST_CHANGED:
                return jQuery.extend({}, state, {
                    needsUpdate: true,
                    articleCount: GlobalHelper.getArticleCount()
                });
            case ActionConstants.ARTICLE_LIST_UPDATE:
                return jQuery.extend({}, state, {
                    needsUpdate: false,
                    articleList: action.value
                });
            case ActionConstants.RESET_PROJECT_RELATED_SETTINGS:
                return jQuery.extend({}, this.getInitialState());
            default:
                break;
        }
        return state;
    }
}

export let ArticleStore: ArticleStoreClass = new ArticleStoreClass(AppDispatcher);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/ArticleStore.ts